// HeyMotek — Signup Page
//
// Visual reference:  ~/heymotek-web/src/pages/signup.astro
// Brand sheet:       ~/heymotek/brand/design-system.md
// Voice doc:         ~/heymotek/brand/voice.md
// Playbook:          ~/hq/PLAYBOOKS/saas-marketing-site-foundations.md
//
// Scope: signup page only. Form + backdrop extracted to components/auth/
// (2026-07-30, shared with GhlInstallPage) — this page's rendered output is
// LOCKED (claim-first hero per Barak's 2026-07-15 ruling) and must not change.
// After successful auth the shared form calls /signup/create-account, then
// this page navigates to '/'; ProtectedRoute decides onboarding vs dashboard.

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { auth } from '@/lib/firebase';
import { BackdropDashboard } from '@/components/auth/BackdropDashboard';
import { SignupForm } from '@/components/auth/SignupForm';

// ─────────────────────────────────────────────────────────────────────
// Right column — proof panel
// ─────────────────────────────────────────────────────────────────────

const proofLogos = [
  { name: 'Google Ads',       icon: '/images/platforms/google-ads.svg' },
  { name: 'Meta',             icon: '/images/platforms/meta.svg' },
  { name: 'WhatsApp',         icon: '/images/platforms/whatsapp.svg' },
  { name: 'Google Analytics', icon: '/images/platforms/google-analytics.svg' },
  { name: 'Shopify',          icon: '/images/platforms/shopify.svg' },
  { name: 'WordPress',        icon: '/images/platforms/wordpress.svg' },
  { name: 'Wix',              icon: '/images/platforms/wix.svg' },
  { name: 'Webflow',          icon: '/images/platforms/webflow.svg' },
];

const outcomeChips = [
  'Every WhatsApp contact matched to its ad source',
  'Won sales sent back to your ad platforms',
  'Connect once, no developer needed',
];

function ProofPanel() {
  return (
    <div className="bg-bg-tint p-8 md:p-12 flex flex-col gap-6 border-t md:border-t-0 md:border-l border-dark/5">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Built for</p>
      <h2 className="text-xl md:text-2xl font-extrabold leading-tight tracking-tight">
        Teams running <span className="text-brand">Google Ads and Meta Ads</span> that bring WhatsApp customers.
      </h2>

      {/* Integration logos grid */}
      <div className="grid grid-cols-4 gap-3">
        {proofLogos.map((p) => (
          <div
            key={p.name}
            className="aspect-square bg-white rounded-xl flex flex-col items-center justify-center gap-1.5 shadow-sm border border-dark/5 p-2"
          >
            <img src={p.icon} alt={p.name} className="w-7 h-7" loading="lazy" />
            <span className="text-[10px] font-semibold text-muted-foreground text-center leading-tight">
              {p.name}
            </span>
          </div>
        ))}
      </div>

      {/* Outcome chips */}
      <div className="space-y-3 mt-2">
        {outcomeChips.map((line) => (
          <div key={line} className="flex items-start gap-2.5 text-sm leading-snug">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
              <Check className="w-3 h-3 text-green-700" strokeWidth={3} />
            </span>
            <span className="font-medium">{line}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────

export function SignupPage() {
  const navigate = useNavigate();

  // If the user is ALREADY signed in when they land here, bounce away.
  // After this initial check, navigation is handled by the form's
  // onComplete so we don't race the create-account API call.
  useEffect(() => {
    if (auth.currentUser) navigate('/', { replace: true });
  }, [navigate]);

  return (
    <div className="bg-bg-tint min-h-screen relative">
      <BackdropDashboard />

      {/* Modal */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 md:p-6">
        <div className="w-full max-w-5xl bg-white rounded-3xl shadow-2xl overflow-hidden grid md:grid-cols-2">
          {/* LEFT — form */}
          <div className="p-8 md:p-12 flex flex-col">
            {/* Logo */}
            <div className="mb-8">
              <span className="font-extrabold text-2xl tracking-tight">
                <span style={{ WebkitTextStroke: '2px #0F172A', color: 'white', paintOrder: 'stroke fill' }}>hey</span>
                <span className="text-brand">motek</span>
              </span>
            </div>

            {/* Greeting */}
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-3 leading-tight">
              Hey motek <span className="inline-block">👋</span>
            </h1>
            <p className="text-muted-foreground text-base leading-relaxed mb-8">
              2X ROAS from your WhatsApp customers. Same ad budget. Automatically.
            </p>

            <SignupForm onComplete={() => navigate('/', { replace: true })} />

            {/* T&C */}
            <p className="mt-6 text-xs text-muted-foreground text-center leading-relaxed">
              By signing up you agree to our{' '}
              <a href="https://heymotek.com/terms/" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                Terms
              </a>{' '}
              and{' '}
              <a href="https://heymotek.com/privacy/" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                Privacy Policy
              </a>
              .
            </p>

            {/* Sign-in */}
            <p className="mt-auto pt-8 text-center text-sm text-muted-foreground">
              Already a <span className="font-bold text-brand">Motek</span>?{' '}
              <a href="/signin" className="font-bold text-foreground hover:text-brand">
                Sign in here.
              </a>
            </p>
          </div>

          {/* RIGHT — proof */}
          <ProofPanel />
        </div>
      </div>
    </div>
  );
}
